import React, { useMemo } from "react";
import { Box, Container, Skeleton, Typography } from "@mui/material";
import { Check } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { useGetWeddingInvitationQuery } from "../../store/services/invitations/invitations.service";
import { PRICING_PLAN_CONFIG } from "../../constants";
import { getInvitationPrice } from "../../utils/getInvitationPrice";
import { formatCurrency } from "../../utils/formatCurrency";
import { INVITATION_PRICING_PLANS_ENUM, Price } from "../../store/services/invitations/dto/invitations.dto";
import { BlackCrown, GoldCrown, PlanBadge } from "../Invitations/styles";
import {
	DiscountBadge,
	FeatureItem,
	FeatureList,
	PriceAmount,
	PricingCard,
	PricingHeader,
	PricingWrapper
} from "../../Components/Modals/PricingModal/styles";
import { round } from "lodash";
import PageIntro from "../../Components/PageIntro";
import ButtonComponent from "../../Components/ButtonComponent";
import NoDataFallback from "../../Components/NoDataFallback";
import { trackEvent } from "../../utils/analytics";
import { PricingPageWrapper } from "./styles";

const PRICING_INVITATION_ID =
	process.env.REACT_APP_ENV === "production" ? "69ce58889b03029444016457" : "69cdc1bd367ad087caed40cd";

const FREE_PLAN: Price = {
	type: INVITATION_PRICING_PLANS_ENUM.FREE,
	name: "Free",
	amount: 0,
	discount: 0,
	is_discount_percentage: false,
	currency: "EGP"
};

const Pricing = () => {
	const navigate = useNavigate();
	const { data, isLoading, isError } = useGetWeddingInvitationQuery({ id: PRICING_INVITATION_ID });

	const plans = useMemo(() => {
		const pricing = data?.invitation?.pricing || [];
		return [FREE_PLAN, ...pricing].sort((a, b) => a.type - b.type);
	}, [data]);

	const handleGetStarted = () => {
		trackEvent("view_invitations", { scope: "pricing page" });
		navigate("/invitation-templates");
	};

	return (
		<PricingPageWrapper>
			<Container maxWidth={"lg"}>
				<PageIntro title={"Simple, transparent pricing"}>
					Choose the plan that fits your special day — upgrade anytime as your celebration grows.
				</PageIntro>
				<Box mt={5} />

				{isLoading && (
					<PricingWrapper>
						{[1, 2, 3].map(i => (
							<PricingCard key={i} style={{ cursor: "default" }}>
								<Skeleton variant={"circular"} width={28} height={28} />
								<Box mt={1.5}>
									<Skeleton variant={"rounded"} width={90} height={26} sx={{ borderRadius: "20px" }} />
								</Box>
								<Box mt={2}>
									<Skeleton variant={"text"} width={"60%"} height={44} />
								</Box>
								<Box mt={1}>
									<Skeleton variant={"text"} width={"100%"} />
									<Skeleton variant={"text"} width={"90%"} />
								</Box>
								<FeatureList>
									{[1, 2, 3, 4, 5].map(line => (
										<Skeleton key={line} variant={"text"} width={line % 2 ? "95%" : "80%"} />
									))}
								</FeatureList>
								<Box height={"20px"} />
								<Box mt={"auto"}>
									<Skeleton variant={"rounded"} height={40} sx={{ borderRadius: "8px" }} />
								</Box>
							</PricingCard>
						))}
					</PricingWrapper>
				)}

				{!isLoading && isError && <NoDataFallback message={"Unable to load pricing right now."} />}

				{!isLoading && !isError && (
					<PricingWrapper>
						{plans.map(plan => {
							const isFree = plan.type === INVITATION_PRICING_PLANS_ENUM.FREE;
							const isSignature = plan.type === INVITATION_PRICING_PLANS_ENUM.SIGNATURE;
							const { description, features } = PRICING_PLAN_CONFIG[plan.type];
							const planFinalAmount = getInvitationPrice(plan.amount, plan.is_discount_percentage, plan.discount);
							const planFinalPrice = isFree ? "Free" : formatCurrency(planFinalAmount, plan.currency);

							return (
								<PricingCard key={plan.type} $signature={isSignature} style={{ cursor: "default" }}>
									{plan.discount > 0 && (
										<DiscountBadge>
											Save{" "}
											{plan.is_discount_percentage ? `${plan.discount}%` : formatCurrency(plan.discount, plan.currency)}
										</DiscountBadge>
									)}

									<PricingHeader>{isSignature ? <GoldCrown /> : <BlackCrown />}</PricingHeader>

									<PlanBadge $signature={isSignature}>{plan.name}</PlanBadge>
									{plan.discount > 0 && <Box position={"relative"} height={"20px"} />}
									<PriceAmount flexWrap={"wrap"}>
										{plan.discount > 0 && (
											<Typography variant={"caption"} className={"old_price"}>
												{formatCurrency(round(plan.amount, 2), plan.currency)}
											</Typography>
										)}
										<Typography variant="h4" fontWeight={800} color={isSignature ? "#8B6914" : "primary.dark"}>
											{planFinalPrice}
										</Typography>
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ visibility: isFree ? "hidden" : "visible" }}
										>
											/ one-time
										</Typography>
									</PriceAmount>

									<Typography variant="body2" color="text.secondary" lineHeight={1.5}>
										{description}
									</Typography>

									<FeatureList>
										{features.map((feature, i) => (
											<FeatureItem key={i}>
												<Check
													sx={{
														fontSize: 16,
														color: isSignature ? "#C9A84C" : "primary.main",
														flexShrink: 0,
														mt: "1px"
													}}
												/>
												<span>{feature}</span>
											</FeatureItem>
										))}
									</FeatureList>
									<Box height={"20px"} />
									<Box mt={"auto"}>
										<ButtonComponent
											variant={"contained"}
											fullWidth
											loading={false}
											onClick={handleGetStarted}
											text={"Get Started"}
											sx={{
												borderRadius: "8px",
												background: isSignature ? "#C9A84C" : undefined,
												"&:hover": {
													background: isSignature ? "#B8963E" : undefined
												}
											}}
										/>
									</Box>
								</PricingCard>
							);
						})}
					</PricingWrapper>
				)}
			</Container>
		</PricingPageWrapper>
	);
};

export default Pricing;
